import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  AppointmentDto,
  AppointmentSlotDto,
  AppointmentSlotStatus,
  AppointmentStatus,
  PropertyApplicationDto,
  PropertyDto,
} from '@workspace/data-types';

import { AuraCountdownComponent } from '@workspace/ng-aura';

@Component({
  selector: 'etxe-discussion-visit-card',
  imports: [
    CommonModule,
    DatePipe,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    AuraCountdownComponent,
  ],
  templateUrl: './discussion-visit-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiscussionVisitCardComponent {
  readonly appointment = input<AppointmentDto | null>(null);
  readonly application = input<PropertyApplicationDto | null>(null);
  readonly isOwner = input<boolean>(false);
  readonly property = input<PropertyDto | null>(null);

  readonly deadline = computed<Date | string | null>(() => {
    const app = this.application();
    if (app?.pendingAction?.deadline) {
      return app.pendingAction.deadline;
    }
    const appt = this.appointment();
    if (!appt) return null;
    if (appt.updatedAt) {
      return new Date(new Date(appt.updatedAt).getTime() + 24 * 60 * 60 * 1000);
    }
    if (appt.createdAt) {
      return new Date(new Date(appt.createdAt).getTime() + 24 * 60 * 60 * 1000);
    }
    return null;
  });

  readonly proposeVisit = output<void>();
  readonly confirmSlot = output<string>();
  readonly requestReschedule = output<string | undefined>();
  readonly markAsVisited = output<void>();
  readonly reportNoShow = output<string | undefined>();

  readonly selectedSlotId = signal<string | null>(null);
  readonly showRescheduleForm = signal<boolean>(false);
  readonly rescheduleReason = signal<string>('');
  readonly showNoShowForm = signal<boolean>(false);
  readonly noShowReason = signal<string>('');
  readonly showHistory = signal<boolean>(false);

  readonly isProposed = computed(() => {
    return this.appointment()?.status === AppointmentStatus.PROPOSED;
  });

  readonly isRescheduled = computed(() => {
    return this.appointment()?.status === AppointmentStatus.RESCHEDULED;
  });

  readonly isConfirmed = computed(() => {
    return this.appointment()?.status === AppointmentStatus.CONFIRMED;
  });

  readonly isCompleted = computed(() => {
    return this.appointment()?.status === AppointmentStatus.COMPLETED;
  });

  readonly isNoShow = computed(() => {
    return this.appointment()?.status === AppointmentStatus.NO_SHOW;
  });

  readonly proposedSlots = computed<AppointmentSlotDto[]>(() => {
    return (this.appointment()?.proposedSlots || []).filter(
      (s) => s.status === AppointmentSlotStatus.PROPOSED,
    );
  });

  readonly declinedSlots = computed<AppointmentSlotDto[]>(() => {
    return (this.appointment()?.proposedSlots || []).filter(
      (s) => s.status === AppointmentSlotStatus.DECLINED,
    );
  });

  readonly confirmedSlot = computed<AppointmentSlotDto | null>(() => {
    return this.appointment()?.confirmedSlot || null;
  });

  readonly isVisitPassed = computed<boolean>(() => {
    const slot = this.confirmedSlot();
    if (!slot) return false;
    return new Date().getTime() >= new Date(slot.startAt).getTime();
  });

  readonly isAddressRevealed = computed<boolean>(() => {
    if (this.isOwner()) return true;
    const slot = this.confirmedSlot();
    if (!slot) return false;
    const startTime = new Date(slot.startAt).getTime();
    const twentyFourHoursBefore = startTime - 24 * 60 * 60 * 1000;
    return new Date().getTime() >= twentyFourHoursBefore;
  });

  toggleHistory(): void {
    this.showHistory.update((v) => !v);
  }

  selectSlot(slotId: string): void {
    if (!this.isOwner() && this.isProposed()) {
      this.selectedSlotId.set(slotId);
      this.showRescheduleForm.set(false);
    }
  }

  onConfirmSlot(): void {
    const slotId = this.selectedSlotId();
    if (slotId) {
      this.confirmSlot.emit(slotId);
    }
  }

  toggleRescheduleForm(): void {
    const next = !this.showRescheduleForm();
    this.showRescheduleForm.set(next);
    if (next) {
      this.selectedSlotId.set(null);
    }
  }

  onRequestReschedule(): void {
    this.requestReschedule.emit(this.rescheduleReason().trim() || undefined);
    this.showRescheduleForm.set(false);
    this.rescheduleReason.set('');
  }

  onProposeVisit(): void {
    this.proposeVisit.emit();
  }

  onMarkAsVisited(): void {
    this.markAsVisited.emit();
  }

  toggleNoShowForm(): void {
    this.showNoShowForm.update((v) => !v);
  }

  onReportNoShow(): void {
    this.reportNoShow.emit(this.noShowReason().trim() || undefined);
    this.showNoShowForm.set(false);
    this.noShowReason.set('');
  }
}
